/**
 * CR500 Phase 2A: ActionsBlock
 * CR500 Phase 6A: Telemetry integration
 * 
 * Renders the action buttons (View deal, T&Cs, Claim deal).
 * Extracted from DealGiftCard for v2 block-based rendering.
 */

import React from "react";
import { ExternalLink, FileText } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { trackCtaEvent } from "@/lib/deal-cards/telemetry";

interface ActionsBlockProps extends DealCardBlockProps<Deal> {
  /** Whether to show the claim button (separate visibility control) */
  showClaimButton?: boolean;
}

export const ActionsBlock: React.FC<ActionsBlockProps> = ({
  deal,
  block,
  context,
  showClaimButton = true,
}) => {
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  const hasProofUrl = deal.proof_urls?.[0];
  const hasTermsUrl = !deal.claim_url && (deal.terms_url || deal.flyer_deeplink_url || deal.source_url);
  const hasClaimUrl = deal.claim_url;
  
  // If no actions to show, don't render
  if (!hasProofUrl && !hasTermsUrl && !hasClaimUrl) {
    return null;
  }

  return (
    <>
      {/* Source buttons section */}
      {(hasProofUrl || hasTermsUrl) && (
        <div 
          className="mt-4 flex flex-wrap gap-2"
          data-block="actions"
          data-block-id={block.id}
          data-component-key="actions"
        >
          {/* Primary action: View deal (proof URL) */}
          {hasProofUrl && (
            <a
              href={deal.proof_urls![0]}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCtaEvent("view", "v2", deal.deal_type)}
              className="rounded-full px-4 py-2 text-sm font-medium bg-black text-white hover:bg-black/90 transition-colors inline-flex items-center gap-1.5"
            >
              View deal
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          
          {/* T&Cs button - uses terms_url if available, falls back to source_url */}
          {hasTermsUrl && (
            <a
              href={deal.terms_url || deal.flyer_deeplink_url || deal.source_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCtaEvent("terms", "v2", deal.deal_type)}
              className="rounded-full px-4 py-2 text-sm font-medium bg-black/5 text-black hover:bg-black/10 transition-colors inline-flex items-center gap-1.5"
            >
              <FileText className="w-3.5 h-3.5" />
              {deal.terms_url ? "T&Cs" : "Source"}
            </a>
          )}
        </div>
      )}
      
      {/* Claim deal button - separate from source buttons */}
      {showClaimButton && hasClaimUrl && (
        <div className="mt-4 flex flex-wrap gap-2" data-section="claim_button">
          <a
            href={deal.claim_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaEvent("claim", "v2", deal.deal_type)}
            className="rounded-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5"
          >
            Claim deal
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
    </>
  );
};

export default ActionsBlock;
